import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { useNavigate } from "react-router-dom";
import {AddRounded, DeleteRounded, DescriptionRounded, DownloadRounded, InfoRounded} from "@mui/icons-material";
import { Button, Stack, useTheme } from "@mui/material";
import { STATE_REPORT_ACTIVE_FILTERS, STATE_REPORT_TOTAL_COUNT, STATE_REPORT_UPDATING } from "../constants/IntermediariesRecoil";
import { formatDate } from "../../../utils/FormatUtils";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { formatAmount, getQuarterlyByKey, onDownloadPrintout } from "./ReportTableUtil";
import { useIntermediariesPrintoutRepository } from "../../../api/repositories/IntermediariesPrintoutRepository";
import { useIntermediariesReportRepository } from "../../../api/repositories/IntermediariesReportRepository";
import { getLocalGridState } from "../../../utils/GridUtils";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { useHasScope } from "../../../../../config/globalConfig";
import {
    SCOPE_FINANCE_CREATE_INTERMEDIARIES_PRINTOUTS,
    SCOPE_FINANCE_DOWNLOAD_INTERMEDIARIES_REPORTS,
    SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS,
    SCOPE_FINANCE_VIEW_INTERMEDIARIES_PRINTOUTS
} from "../constants/ConstantsAuth";
import FinanceDialog from "../../../component/FinanceDialog";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";


const ReportTable = () => {
    const UNIQUE_ID = "fnn-intermediaries-report";

    const {t} = useTranslation('finance', {keyPrefix: 'intermediaries'});
    const {  dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);

    const {getReports, deleteIntermediaryPrintoutReport} = useIntermediariesReportRepository();
    const {downloadPrintoutCSV} = useIntermediariesPrintoutRepository();
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setTotalDataCount = useSetRecoilState(STATE_REPORT_TOTAL_COUNT);
    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [reportToDelete, setReportToDelete] = useState({ id: '', name: '' })

   
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");

    const columnDefs : ColDef[] = [
        {
            field: 'year',
            headerName: t('tables.report.year'),
            headerTooltip: t('tables.report.year'),
            unSortIcon: true,
        },
        {
            field: 'quarterly',
            headerName: t('tables.report.quarterly'),
            headerTooltip: t('tables.report.quarterly'),
            cellRenderer: (params: any) => getQuarterlyByKey(params.value),
            tooltipValueGetter: (params: any) => getQuarterlyByKey(params.value),
            unSortIcon: true,
        },
        {
            field: 'createdDate',
            headerName: t('tables.report.reportCreationDate'),
            headerTooltip: t('tables.report.reportCreationDate'),
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value),
            tooltipValueGetter: (params: any) => formatDate(params.value)
        },
        {
            field: 'printoutCount',
            headerName: t('tables.report.printoutsCount'),
            headerTooltip: t('tables.report.printoutsCount'),
            tooltipValueGetter: (params: any) => params.value,
            unSortIcon: true
        },
        {
            field: 'totalCommissions',
            headerName: t('tables.report.totalCommissions'),
            headerTooltip: t('tables.report.totalCommissions'),
            cellRenderer: (params: any) => formatAmount(params.value, 0.01),
            tooltipValueGetter: (params: any) => formatAmount(params.value, 0.01),
            unSortIcon: true
        },
        {
            field: "legalEntity", headerName: t("tables.report.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                        <FinanceDynamicActions>
                            {hasScope(SCOPE_FINANCE_VIEW_INTERMEDIARIES_PRINTOUTS) &&
                                <IconButton title={t('preview.buttons.viewDetails')} sx={{color: theme.palette.accent.main}} onClick={() => {
                                    window.open('/finance/intermediaries/report/' + params.data.id, "_blank");
                                }}>
                                    <DescriptionRounded/>
                                </IconButton>
                            }
                            {hasScope(SCOPE_FINANCE_DOWNLOAD_INTERMEDIARIES_REPORTS) &&
                                <IconButton title={t('preview.buttons.download')} sx={{color: theme.palette.accent.main}} onClick={() => {
                                    downloadPrintoutCSV(params.data.id).then(response => {
                                        onDownloadPrintout(response, params.data.year, params.data.quarterly);
                                    }).catch(e => {
                                        dispatchGlobalData({
                                            type: setSnackbar,
                                            payload: {
                                                snackbar: {
                                                    message: t("printout-wizard.snackbars.handleErrors.errors.generic"),
                                                    type: snackbarTypes.error,
                                                    show: true,
                                                    autoClose: true,
                                                }
                                            }
                                        });
                                    })
                                }}>
                                    <DownloadRounded/>
                                </IconButton>
                            }
                            {hasScope(SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS) &&
                                <IconButton title={t('preview.buttons.delete')} sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                setOpenDeleteModal(true);
                                                setReportToDelete({ id: params.data.id, name: params.data.year + " "+ getQuarterlyByKey(params.data.quarterly) });
                                            }}>
                                    <DeleteRounded/>
                                </IconButton>
                            }
                       </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t("tables.report.noRows.title"),
            content: t("tables.report.noRows.content"),
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating])


    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;
                        setTotalDataCount(result.total);
                        if (result.total === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.total
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }


    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    const toolbar = {

        renderActions: () => (
            <Stack>
                {hasScope(SCOPE_FINANCE_CREATE_INTERMEDIARIES_PRINTOUTS) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                        navigate("/finance/intermediaries/printouts-wizard")
                    }} startIcon={<AddRounded/>}>{t('buttons.createPrintout')}
                    </Button>
                }
            </Stack>
        )
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));
                }}
            />
            <FinanceDialog
                title={t('preview.buttons.deleteReportTitle')}
                content={t('preview.buttons.deleteReportContent', {reportName: reportToDelete.name})}
                cancelButton={t('preview.buttons.deleteCancel')}
                callback={() => {
                    deleteIntermediaryPrintoutReport(reportToDelete.id).then((res: any) => {
                        if (res.status === 'OK') {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteSuccessReport'),
                                        type: snackbarTypes.success,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                            setUpdating(true);
                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteErrorReport'),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                        }
                    })
                }}
                confirmButton={t('preview.buttons.deleteConfirm')}
                open={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false)
                }}
                titleIcon={<InfoRounded sx={{color: 'accent.main'}}/>}
            />
        </>
    );
}

export default ReportTable;